import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_ITEMS = [
  { label: "Home", id: "home" },
  { label: "For Everyone", id: "for-everyone" },
  { label: "Meet Sophie", id: "meet-sophie" },
  { label: "Our Story", id: "our-story" },
  { label: "Contact", id: "contact" },
];

const testId = (label, mobile) =>
  `${mobile ? "mobile-nav" : "nav"}-${label.toLowerCase().replace(/\s+/g, "-")}-link`;

/** Tracks which homepage section is currently in view for the active underline. */
function useActiveSection(pathname) {
  const [active, setActive] = useState("home");
  useEffect(() => {
    if (pathname !== "/") {
      setActive("");
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
    );
    const timer = setTimeout(() => {
      NAV_ITEMS.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) observer.observe(el);
      });
    }, 100);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [pathname]);
  return active;
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const { pathname, hash } = useLocation();
  const active = useActiveSection(pathname);

  useEffect(() => {
    setOpen(false);
  }, [pathname, hash]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleNavClick = (e, item) => {
    setOpen(false);
    if (pathname !== "/") return;
    e.preventDefault();
    const reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const behavior = reduce ? "auto" : "smooth";
    if (item.id === "home") {
      window.scrollTo({ top: 0, behavior });
      window.history.replaceState(null, "", "/");
      return;
    }
    const el = document.getElementById(item.id);
    if (el) {
      el.scrollIntoView({ behavior, block: "start" });
      window.history.replaceState(null, "", `/#${item.id}`);
    }
  };

  return (
    <header
      className="sticky top-0 z-50 bg-brand-cream/95 backdrop-blur-sm"
      data-testid="site-header"
    >
      <div className="symp-container flex h-[84px] items-center justify-between md:h-[96px] lg:h-[112px]">
        <Link
          to="/"
          aria-label="SympTrack home"
          data-testid="header-logo-link"
          onClick={(e) => handleNavClick(e, NAV_ITEMS[0])}
          className="shrink-0"
        >
          <img
            src="/images/logo.png"
            alt="SympTrack, Powered by MyRelief"
            width="1536"
            height="864"
            className="h-auto w-[190px] md:w-[240px] lg:w-[280px]"
          />
        </Link>
        <nav aria-label="Primary navigation" className="hidden lg:block">
          <ul className="flex items-center" style={{ gap: "clamp(24px, 3vw, 44px)" }}>
            {NAV_ITEMS.map((item) => (
              <li key={item.id}>
                <Link
                  to={`/#${item.id}`}
                  data-testid={testId(item.label, false)}
                  onClick={(e) => handleNavClick(e, item)}
                  aria-current={active === item.id ? "page" : undefined}
                  className="nav-link px-1"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <button
          type="button"
          className="inline-flex h-11 w-11 items-center justify-center rounded-full text-brand-teal lg:hidden"
          aria-expanded={open}
          aria-controls="mobile-nav"
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="mobile-menu-button"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? (
            <X className="h-6 w-6" aria-hidden="true" />
          ) : (
            <Menu className="h-6 w-6" aria-hidden="true" />
          )}
        </button>
      </div>
      {open && (
        <nav
          id="mobile-nav"
          aria-label="Mobile navigation"
          className="border-t border-brand-teal/10 bg-brand-cream shadow-[0_24px_40px_rgba(7,88,102,0.12)] lg:hidden"
          data-testid="mobile-nav"
        >
          <ul className="symp-container flex flex-col py-3">
            {NAV_ITEMS.map((item) => (
              <li key={item.id}>
                <Link
                  to={`/#${item.id}`}
                  data-testid={testId(item.label, true)}
                  onClick={(e) => handleNavClick(e, item)}
                  aria-current={active === item.id ? "page" : undefined}
                  className="flex min-h-[48px] items-center rounded-xl px-3 py-3 text-lg font-semibold text-brand-teal aria-[current=page]:text-brand-coral"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  );
}
